import closeReadingPane from 'mini-mail-reading-pane-store/lib/utils/closeReadingPane';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import isItemView from 'mini-mail-list-store/lib/utils/isItemView';
import type Module from 'mini-common-schema/lib/schema/Module';
import switchModule from 'owa-mail-store/lib/actions/switchModule';
import { getReadingPaneStore } from 'mini-mail-reading-pane-store/lib/store/Store';
import { getValidFolderNameOrIdWithFallback } from 'mini-mail-folder-actions/lib/utils/getValidFolderNameOrIdWithFallback';
import { isRowLoadedInFolder } from '../utils/isRowLoadedInFolder';
import { lazyHideBottomSheet } from 'mini-bottom-sheet-animated';
import { MAIL_LOAD_READING_PANE_ON_REFRESH_START } from 'mini-mail-workflow-tracking/lib/constants/ListViewWorkflowConstants';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { singleSelectMailItem } from 'mini-mail-list-actions';
import { type TableQueryType } from 'mini-mail-list-store/lib/store/schema/TableQuery';
import { tryHydratingListView } from '../utils/tryHydratingListView';

export interface MailItemViewReadingPaneRouteParameters {
    folderId: string;
    itemId: string;
}

export async function mailItemViewReadingPaneRouteEntryHandler(
    params: MailItemViewReadingPaneRouteParameters
): Promise<void> {
    // When we reach a mail folder route, switch selected module to mail
    switchModule(0);

    const { folderId, itemId } = params;

    // When reading pane is opened from search view, we needn't fallback to folder view.
    if (getSelectedTableView().tableQuery?.type === 1) {
        return;
    }

    // For a conversation view user this is an invalid route, so we terminate here.
    if (!isItemView()) {
        return;
    }

    // If we are trying to open an already opened item, don't recreate the viewState.
    // This happens on pressing hardware back button after we open compose window through R/RA/F actions.
    if (
        getReadingPaneStore().itemReadingPaneViewState &&
        getReadingPaneStore().itemReadingPaneViewState.itemId === itemId
    ) {
        return;
    }

    const effectiveFolderNameOrId = getValidFolderNameOrIdWithFallback(folderId);
    if (!isRowLoadedInFolder(itemId, effectiveFolderNameOrId)) {
        // Need to await for tableView to be populated
        await tryHydratingListView(effectiveFolderNameOrId);
    }

    if (isRowLoadedInFolder(itemId, effectiveFolderNameOrId)) {
        singleSelectMailItem(itemId);
        signalWorkflowEvent(
            MAIL_LOAD_READING_PANE_ON_REFRESH_START,
            { isItemView: true, itemId },
            { isCore: true }
        );
    }
}

export async function mailItemViewReadingPaneRouteExitHandler(
    oldRoute: string[],
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    oldParams: any,
    newRoute: string[],
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    newParams: any
): Promise<boolean> {
    // If our last visited route was attachment preview or compose through R/RA/F actions then on clicking hardware back button,
    // we will go to these old routes again which we don't want so we close the reading pane in this case.
    if (
        (newParams && newParams.draftId) ||
        (newRoute && newRoute.includes('compose')) ||
        newRoute?.includes('attachment')
    ) {
        closeReadingPane();
        return true;
    }
    // Don't clear the store if the new route is also an item reading pane
    // (Triggered when user makes left or right swipe actions in the item reading pane and then presses hardware back button.)
    // This is to avoid triggers which adds extra 'mail/<folderId>' routes unnecessarily
    if (newParams && newParams.itemId) {
        lazyHideBottomSheet.importAndExecute();
    } else {
        closeReadingPane();
    }
    return false;
}
